import supabase from '../lib/supabase';

/**
 * Derive the deployment environment from the current URL.
 * @returns {'localhost' | 'beta' | 'prod'}
 */
function getEnvironment() {
  try {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost';
    if (hostname.includes('beta')) return 'beta';
    return 'prod';
  } catch (_) {
    return 'prod';
  }
}

/**
 * Log a usage event to the usage_events table.
 *
 * @param {object} params
 * @param {string}  params.eventType   - e.g. 'copy_results', 'export_excel'
 * @param {string}  params.calculator  - 'basic' or 'pro'
 * @param {object}  [params.user]      - Supabase user object (from useAuth)
 * @param {string}  [params.planId]    - Loaded plan UUID, if any
 * @param {string}  [params.planName]  - Loaded plan name, if any
 * @param {object}  [params.metadata]  - Any extra key/value pairs for future use
 */
export const logUsageEvent = async ({
  eventType,
  calculator,
  user = null,
  planId = null,
  planName = null,
  metadata = {},
}) => {
  try {
    // Always resolve the current session so the Supabase client has a valid JWT
    // (the user prop may be stale or missing the token the client needs for RLS)
    let userId = user?.id || null;
    let email  = user?.email || null;

    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id || null;
      email  = session?.user?.email || email;
    }

    const row = {
      user_id:      userId,
      email:        email,
      event_type:   eventType,
      calculator,
      page_context: window.location.href,
      environment:  getEnvironment(),
      plan_id:      planId || null,
      plan_name:    planName || null,
      metadata:     metadata || {},
    };

    const { error } = await supabase.from('usage_events').insert([row]);

    if (error) {
      console.error('[logUsageEvent] INSERT failed:', error.message, error.code, error.details, row);
      showBetaToast(`Usage log failed: ${error.message} (${eventType})`);
      return false;
    }
    return true;
  } catch (e) {
    console.error('[logUsageEvent] exception:', e.message);
    showBetaToast(`Usage log exception: ${e.message}`);
    return false;
  }
};

/**
 * Show a visible toast message — only on Beta URLs, so end-users on Prod never see it.
 */
const showBetaToast = (msg) => {
  try {
    if (!window.location.hostname.includes('beta')) return;
    const el = document.createElement('div');
    // Plain text only — this is a raw DOM node, so no React/SVG icon can go here.
    el.textContent = msg;
    Object.assign(el.style, {
      position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
      background: '#b91c1c', color: '#fff', padding: '10px 20px', borderRadius: '8px',
      fontSize: '13px', zIndex: '99999', maxWidth: '90vw', textAlign: 'center',
      boxShadow: '0 4px 12px rgba(0,0,0,.3)',
    });
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 8000);
  } catch (_) { /* ignore */ }
};
